// src/utils/appHelpers.js
import { API_BASE, EXEC_APPENDER, PASS_MARK } from "../utils/constants";
import { fetchTextWithDiagnostics, safeArr } from "../utils/helpers";
import { mapCategoryItem } from "../utils/mappers";

export async function fetchCategorySummary({ token, appName, section, category, onUnauthorized }) {
  const base = API_BASE.replace(/\/$/, "");
  const qUrl = `${base}/exams${EXEC_APPENDER}/${encodeURIComponent(appName)}` +
    `/${encodeURIComponent(section)}/${encodeURIComponent(category)}` +
    `?CurrentExamID=0&prevNext=Next`.replace(/\+/g, "%2B");
  const qRes = await fetchTextWithDiagnostics(qUrl, { headers: { Authorization: token } });
  let totalQuestions = 0;
  if (qRes.ok) {
    try {
      const qArr = JSON.parse(qRes.text);
      totalQuestions = safeArr(qArr).length;
    } catch {}
  }
  const url = `${base}/exams${EXEC_APPENDER}/${encodeURIComponent(appName)}` +
    `/users/search_exam_exec${EXEC_APPENDER}` +
    `?category_name=${encodeURIComponent(category)}` +
    `&section_name=${encodeURIComponent(section)}` +
    `&is_completed=N`;
  const res = await fetchTextWithDiagnostics(url, { headers: { Authorization: token } });
  if (res.status === 401) {
    onUnauthorized?.();
    return { category, section, stats: { total: totalQuestions, right: 0, wrong: 0, un: totalQuestions, accuracy: 0, status: "Unanswered" } };
  }
  if (!res.ok) {
    return { category, section, stats: { total: totalQuestions, right: 0, wrong: 0, un: totalQuestions, accuracy: 0, status: "Unanswered" } };
  }
  try {
    const arr = JSON.parse(res.text);
    const list = safeArr(arr);
    if (list.length === 0)
      return { category, section, stats: { total: totalQuestions, right: 0, wrong: 0, un: totalQuestions, accuracy: 0, status: totalQuestions > 0 ? "Unanswered" : "Unanswered" } };
    const latest = list.reduce((best, cur) => {
      const bId = Number(best?.exec_id ?? best?.execId ?? -1);
      const cId = Number(cur?.exec_id ?? cur?.execId ?? -1);
      return cId > bId ? cur : best;
    });
    const childs = safeArr(latest?.lstChilds || latest?.lstChild || latest?.children || latest?.childs);
    let right = 0, wrong = 0;
    for (const c of childs) {
      const rw = String(c?.right_or_wrong ?? "").toLowerCase().trim();
      if (rw === "right" || rw === "1" || rw === "yes") right++;
      else if (rw === "wrong" || rw === "0" || rw === "no") wrong++;
    }
    const un = Math.max(0, totalQuestions - right - wrong);
    const total = totalQuestions > 0 ? totalQuestions : right + wrong + un;
    const accuracy = total ? Math.round((right / total) * 100) : 0;
    const status = accuracy >= PASS_MARK ? "Pass" : right === 0 && wrong === 0 && un === 0 ? "Unanswered" : "Fail";
    return { category, section, stats: { total, right, wrong, un, accuracy, status } };
  } catch {
    return { category, section, stats: { total: totalQuestions, right: 0, wrong: 0, un: totalQuestions, accuracy: 0, status: "Unanswered" } };
  }
}

export async function mapInBatches(arr, batchSize, fn) {
  const results = [];
  for (let i = 0; i < arr.length; i += batchSize) {
    const batch = arr.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch.map(fn));
    results.push(...batchResults);
  }
  return results;
}

export async function fetchQuestionsCount({ token, appName, section, category, onUnauthorized }) {
  const base = API_BASE.replace(/\/$/, "");
  const url = `${base}/exams${EXEC_APPENDER}/${encodeURIComponent(appName)}/${encodeURIComponent(section)}/${encodeURIComponent(category)}?CurrentExamID=0&prevNext=Next`.replace(/\+/g, "%2B");
  const res = await fetchTextWithDiagnostics(url, { headers: { Authorization: token } });
  if (res.status === 401) return onUnauthorized?.(), 0;
  try {
    const arr = JSON.parse(res.text);
    return (Array.isArray(arr) ? arr : []).length;
  } catch {
    return 0;
  }
}

export function useDebugRenders(state) {
  const renderCount = React.useRef(0);
  const prevState = React.useRef(state);
  renderCount.current += 1;
  console.log('RENDER COUNT:', renderCount.current);
  console.log('State:', {
    token: state.token ? 'exists' : 'null',
    view: state.view ? state.view.type : 'null',
    questionMode: state.questionMode,
    sectionsLength: state.sections.length,
    catMapKeys: Object.keys(state.catMap).length,
    activeSection: state.activeSection,
    loadingData: state.loadingData,
    catSummariesKeys: Object.keys(state.catSummaries).length,
    loadingSummaries: state.loadingSummaries,
    sectionTotalsKeys: Object.keys(state.sectionTotals).length,
    loadingSectionTotals: state.loadingSectionTotals
  });
  const changes = [];
  if (state.token !== prevState.current.token) changes.push('token');
  if (state.view !== prevState.current.view) changes.push('view');
  if (state.questionMode !== prevState.current.questionMode) changes.push('question Mode');
  if (state.sections !== prevState.current.sections) changes.push('sections');
  if (state.catMap !== prevState.current.catMap) changes.push('catMap');
  if (state.activeSection !== prevState.current.activeSection) changes.push('activeSection');
  if (state.loadingData !== prevState.current.loadingData) changes.push('loadingData');
  if (state.catSummaries !== prevState.current.catSummaries) changes.push('catSummaries');
  if (state.loadingSummaries !== prevState.current.loadingSummaries) changes.push('loadingSummaries');
  if (state.sectionTotals !== prevState.current.sectionTotals) changes.push('sectionTotals');
  if (state.loadingSectionTotals !== prevState.current.loadingSectionTotals) changes.push('loadingSectionTotals');
  console.log('🔥 Changes causing this render:', changes.length > 0 ? changes.join(', ') : 'NONE - SHOULD NOT RENDER!');
  prevState.current = state;
}

export function getCategoriesForSection(catMap, section) {
  const raw = catMap?.[section] || [];
  const items = Array.isArray(raw) ? raw : Object.values(raw);
  return items.map(mapCategoryItem);
}
